import { idbGet, idbSet } from './idb';
/**
 * Player profile state — streak, history, credits, settings, identity.
 * All persisted to IndexedDB; debounced writeback.
 */
import { type Store, createStore } from './store';

export interface SolveRecord {
  date: string; // UTC YYYY-MM-DD
  timeMs: number; // solve time in ms
  attempts: number;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  mode: string;
}

export interface ProfileState {
  /** Anonymous local user id; minted on first run. */
  uid: string;
  /** Streak in consecutive UTC days. */
  streak: number;
  /** Longest streak ever. */
  bestStreak: number;
  /** UTC date of last solve. */
  lastSolveDate: string | null;
  /** Freeze tokens (earned every Sunday solve). */
  freezeTokens: number;
  /** Cached country code, ISO 3166-1 alpha-2 (uppercase). */
  country: string;
  /** ISO-639 locale chosen by user; null = auto. */
  localeOverride: string | null;
  /** 'light' | 'dark' | 'auto' | unlocked theme id. */
  theme: string;
  /** Available credits. */
  credits: number;
  /** Date last seen for daily credit refill (YYYY-MM-DD UTC). */
  creditsRefilledAt: string | null;
  /** Number of ad-watched bonuses today. */
  adBonusesToday: number;
  /** Solved-puzzle history (newest last; capped to 730 = ~2yr). */
  history: SolveRecord[];
  /** Total solves count (lifetime, including pre-cap). */
  totalSolves: number;
  /** Elo-style rating. Starts at 1000. */
  rating: number;
  /** Onboarding completed? */
  onboarded: boolean;
  /** Notification permission decision recorded? */
  notifAsked: boolean;
  /** Invite code applied (one-time). */
  inviteUsed: string | null;
  /** Player's own invite code (generated). */
  myInviteCode: string;
  /** Reduced motion / high contrast user pref override (overrides system). */
  a11y: {
    reduceMotion: 'auto' | 'on' | 'off';
    highContrast: 'auto' | 'on' | 'off';
    colorBlind: 'off' | 'protanopia' | 'deuteranopia' | 'tritanopia';
  };
  /** Cookie consent for EU/UK web — null = not asked. */
  cookieConsent: 'accepted' | 'declined' | null;
  /** First seen ISO timestamp. */
  createdAt: string;
}

function newCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  const r = crypto.getRandomValues(new Uint8Array(6));
  for (let i = 0; i < 6; i++) out += alphabet[r[i]! % alphabet.length];
  return `FUDETO-${out}`;
}

function newUid(): string {
  const c = (typeof crypto !== 'undefined' ? crypto : null) as
    | (Crypto & { randomUUID?: () => string })
    | null;
  if (c?.randomUUID) return c.randomUUID();
  if (c) {
    const r = c.getRandomValues(new Uint8Array(16));
    return Array.from(r)
      .map((b: number) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function detectCountry(): string {
  try {
    const lang = navigator.language;
    const m = /-([A-Z]{2})/.exec(lang);
    if (m?.[1]) return m[1];
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz?.startsWith('Asia/Tokyo')) return 'JP';
    if (tz?.startsWith('Asia/Taipei')) return 'TW';
    if (tz?.startsWith('Asia/Shanghai')) return 'CN';
    if (tz?.startsWith('Asia/Seoul')) return 'KR';
    if (tz?.startsWith('America/')) return 'US';
    if (tz?.startsWith('Europe/')) return 'GB';
  } catch {
    /* fall through */
  }
  return 'US';
}

export function defaultProfile(): ProfileState {
  return {
    uid: newUid(),
    streak: 0,
    bestStreak: 0,
    lastSolveDate: null,
    freezeTokens: 0,
    country: detectCountry(),
    localeOverride: null,
    theme: 'auto',
    credits: 3,
    creditsRefilledAt: null,
    adBonusesToday: 0,
    history: [],
    totalSolves: 0,
    rating: 1000,
    onboarded: false,
    notifAsked: false,
    inviteUsed: null,
    myInviteCode: newCode(),
    a11y: { reduceMotion: 'auto', highContrast: 'auto', colorBlind: 'off' },
    cookieConsent: null,
    createdAt: new Date().toISOString(),
  };
}

const KEY = 'profile.v1';
let _store: Store<ProfileState> | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

export async function initProfile(): Promise<Store<ProfileState>> {
  if (_store) return _store;
  const persisted = await idbGet<ProfileState>(KEY);
  const initial = persisted ? { ...defaultProfile(), ...persisted } : defaultProfile();
  _store = createStore<ProfileState>(initial);
  _store.subscribe((s) => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => idbSet(KEY, s), 200);
  });
  // First-run persist so uid + inviteCode are stable.
  if (!persisted) await idbSet(KEY, initial);
  return _store;
}

export function profile(): Store<ProfileState> {
  if (!_store) throw new Error('profile not initialized — call initProfile() in boot');
  return _store;
}

/** Update streak after a successful solve. Returns the new streak number. */
export function recordSolve(record: SolveRecord): number {
  const s = profile();
  const cur = s.get();
  const today = record.date;
  const yesterday = utcDaysAgo(today, 1);
  let newStreak = cur.streak;
  if (cur.lastSolveDate === today) {
    // already counted today
  } else if (cur.lastSolveDate === yesterday || cur.lastSolveDate === null) {
    newStreak = cur.lastSolveDate === yesterday ? cur.streak + 1 : 1;
  } else {
    // missed days. Spend a freeze token if we have one and gap is exactly 1 extra day.
    if (cur.freezeTokens > 0 && utcDaysAgo(today, 2) === cur.lastSolveDate) {
      newStreak = cur.streak + 1;
      s.set({ freezeTokens: cur.freezeTokens - 1 });
    } else {
      newStreak = 1;
    }
  }
  const newHistory = cur.history.concat(record).slice(-730);
  s.set({
    streak: newStreak,
    bestStreak: Math.max(cur.bestStreak, newStreak),
    lastSolveDate: today,
    history: newHistory,
    totalSolves: cur.totalSolves + 1,
  });
  // Sunday solve = +1 freeze token (max 3 banked).
  if (new Date(today + 'T00:00:00Z').getUTCDay() === 0) {
    const fp = profile().get();
    if (fp.freezeTokens < 3) profile().set({ freezeTokens: fp.freezeTokens + 1 });
  }
  return newStreak;
}

function utcDaysAgo(yyyyMmDd: string, days: number): string {
  const d = new Date(yyyyMmDd + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/** Refill credits if a new UTC day has begun. */
export function refillCreditsIfNewDay(todayUtc: string): void {
  const p = profile();
  const cur = p.get();
  if (cur.creditsRefilledAt === todayUtc) return;
  // 3 credits/day baseline (Pro raises to 20 — flag in future).
  p.set({ credits: 3, creditsRefilledAt: todayUtc, adBonusesToday: 0 });
}

/** Spend 1 credit; returns false if no credits. */
export function spendCredit(): boolean {
  const p = profile();
  const cur = p.get();
  if (cur.credits <= 0) return false;
  p.set({ credits: cur.credits - 1 });
  return true;
}

/** Award credits from a rewarded ad watch (up to 4 ad bonuses/day). */
export function grantAdBonus(amount = 5): boolean {
  const p = profile();
  const cur = p.get();
  if (cur.adBonusesToday >= 4) return false;
  p.set({ credits: cur.credits + amount, adBonusesToday: cur.adBonusesToday + 1 });
  return true;
}
